import { z } from 'zod';

export type JsonPrimitive = boolean | number | string | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

const isJsonValue = (value: unknown): value is JsonValue => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return true;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).every(isJsonValue);
  }
  return false;
};

export const jsonValueSchema = z.custom<JsonValue>(isJsonValue, 'Expected a JSON-safe value');
export const jsonObjectSchema = z.record(z.string(), jsonValueSchema);

export const FINANCE_TOOL_NAMES = [
  'list_accounts',
  'list_balances',
  'list_categories',
  'list_currencies',
  'list_tags',
  'query_expenses',
  'query_incomes',
  'query_account_transfers',
  'query_currency_exchanges',
] as const;

export const financeToolNameSchema = z.enum(FINANCE_TOOL_NAMES);
export type FinanceToolName = z.infer<typeof financeToolNameSchema>;

export const roomCodeSchema = z.string().regex(/^\d{6}$/);
export const sessionIdSchema = z.string().uuid();
export const requestIdSchema = z.string().uuid();
export const opaqueTokenSchema = z.string().min(32).max(256);
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const isoDateTimeSchema = z.string().datetime({ offset: true });

const jsonSchemaMetaKeys = new Set(['$schema', 'title', 'description', 'default']);

interface JsonSchemaConversion {
  input: (...args: never[]) => Record<string, unknown>;
  output: (...args: never[]) => Record<string, unknown>;
}

/** MCP clients that treat `type` as a string reject JSON Schema type arrays such as `["string","null"]`. */
export const rewriteNullTypeArrays = (node: unknown): unknown => {
  if (Array.isArray(node)) {
    return node.map(rewriteNullTypeArrays);
  }
  if (!node || typeof node !== 'object') {
    return node;
  }

  const rewritten: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    rewritten[key] = rewriteNullTypeArrays(value);
  }

  const types = rewritten['type'];
  if (!Array.isArray(types) || !types.includes('null') || types.length < 2) {
    return rewritten;
  }

  const rest: Record<string, unknown> = {};
  const meta: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rewritten)) {
    if (key === 'type' || key === 'anyOf') {
      continue;
    }
    if (jsonSchemaMetaKeys.has(key)) {
      meta[key] = value;
    } else {
      rest[key] = value;
    }
  }

  return {
    ...meta,
    anyOf: [...types.filter((type) => type !== 'null').map((type) => ({ type, ...rest })), { type: 'null' }],
  };
};

export const withSingleTypeJsonSchema = <T extends z.ZodType>(schema: T): T => {
  const jsonSchema = (schema as unknown as { '~standard': { jsonSchema?: JsonSchemaConversion } })['~standard']
    .jsonSchema;
  if (!jsonSchema) {
    return schema;
  }

  const originalInput = jsonSchema.input.bind(jsonSchema);
  const originalOutput = jsonSchema.output.bind(jsonSchema);
  jsonSchema.input = (...args) => rewriteNullTypeArrays(originalInput(...args)) as Record<string, unknown>;
  jsonSchema.output = (...args) => rewriteNullTypeArrays(originalOutput(...args)) as Record<string, unknown>;
  return schema;
};

export const sessionKeySchema = z
  .string()
  .min(32)
  .max(256)
  .describe('Cryptographically secure session key generated by the App.');
export const sessionKeyHashSchema = z
  .string()
  .min(32)
  .max(256)
  .describe('SHA-256 hash of the session key, used for relay lookup.');
export const mcpKeySchema = opaqueTokenSchema.describe(
  'Cryptographically secure MCP key generated by the App and shared with the MCP client after pairing.',
);

export const createSessionRequestSchema = z
  .object({
    sessionKey: sessionKeySchema,
    mcpKey: mcpKeySchema,
    paired: z.boolean().optional(),
  })
  .strict();
export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;

export const createSessionResponseSchema = z
  .object({
    roomCode: roomCodeSchema,
    expiresAt: isoDateTimeSchema,
  })
  .strict();
export type CreateSessionResponse = z.infer<typeof createSessionResponseSchema>;

export const connectToFinanceRoomDescription =
  'Pair with the six-digit pairing code shown in Finance. Call this first, then pass the returned mcpKey to every other Finance tool. expiresAt is when this pairing ends; after that, start a new session and pair again. These tools are read-only: they cannot change data. Current account stock is available from list_balances.';

export const financeMcpToolDescriptions = {
  list_accounts:
    'List visible accounts as ID and name only (metadata only, no amounts). Hidden accounts are omitted. Use list_balances for current amounts per currency.',
  list_balances:
    'Get current balances for a single visible account, per currency. accountId is required (from list_accounts). amount is in currencyCode, not converted to the workspace default. incomes and expenses are lifetime totals in that same currency. Optional currencyCode narrows to one currency. Rows where amount, incomes, and expenses are all 0 are omitted. This is not a date-range query and does not use the 30-day default.',
  list_categories:
    'List visible income and expense categories. Hidden categories and debt/credit types are omitted. parentId is null for top-level categories. Query includeCategoryIds/excludeCategoryIds match a listed category or any of its children.',
  list_currencies:
    'List visible currencies. isDefault marks the single workspace default currency. Expense and income rate values convert amount into that default (rate is 1 when currencyCode is the default). defaultCurrencyCode repeats the default so clients need not infer it from isDefault.',
  list_tags:
    'List expense tags for query_expenses includeTagIds/excludeTagIds. Incomes have no tags. An expense with an empty tags array is untagged.',
  query_expenses:
    'Query expense records (supports tag filters). Date window: omit both dateFrom and dateTo for the last 30 inclusive calendar days ending today; dateFrom without dateTo runs through today; dateTo without dateFrom is all matching history until that day. Dates are inclusive YYYY-MM-DD. amountFrom/amountTo filter the native amount in currencyCode, not the default-currency equivalent. includeTagIds is AND (the expense must have every listed tag); excludeTagIds drops expenses that have any listed tag. includeCategoryIds is OR and includes child categories. Each item has amount in currencyCode, rate (units of defaultCurrencyCode per 1 currencyCode), and amountInDefaultCurrency (amount * rate). Compare mixed currencies with amountInDefaultCurrency. Pagination: offset/limit (max 100), totalCount, hasMore, nextOffset.',
  query_incomes:
    'Query income records (no tag filters). Date window: omit both dateFrom and dateTo for the last 30 inclusive calendar days ending today; dateFrom without dateTo runs through today; dateTo without dateFrom is all matching history until that day. Dates are inclusive YYYY-MM-DD. currencyCode filters the native income currency. includeCategoryIds is OR and includes child categories. Each item has amount in currencyCode, rate (units of defaultCurrencyCode per 1 currencyCode), and amountInDefaultCurrency (amount * rate). Pagination: offset/limit (max 100), totalCount, hasMore, nextOffset.',
  query_account_transfers:
    'Query same-currency moves between two accounts. amount is in currencyCode on both sides; there is no FX rate and this is not an expense or income. defaultCurrencyCode is the workspace default for context only. Date window: omit both dates for the last 30 inclusive days ending today; dateFrom without dateTo runs through today; dateTo alone is history until that day.',
  query_currency_exchanges:
    'Query currency exchanges on one account: fromAmount of fromCurrencyCode became toAmount of toCurrencyCode. The pair rate is toAmount / fromAmount between those two currencies, not versus the workspace default. defaultCurrencyCode is included for context only. Date window: omit both dates for the last 30 inclusive days ending today; dateFrom without dateTo runs through today; dateTo alone is history until that day.',
} as const satisfies Record<FinanceToolName, string>;

export const connectRoomInputSchema = z
  .object({
    code: roomCodeSchema.describe(
      'Six-digit pairing code shown in the Finance AI session UI. Use this only for initial pairing.',
    ),
  })
  .strict();
export const connectRoomOutputSchema = z
  .object({
    mcpKey: mcpKeySchema.describe(
      'Cryptographically secure MCP secret. Pass this as mcpKey on every subsequent Finance tool call via MCP.',
    ),
    expiresAt: isoDateTimeSchema.describe('When this pairing expires. Pair again with a new session after this time.'),
  })
  .strict();

const positiveIdSchema = z.number().int().positive();
const idListSchema = z.array(positiveIdSchema).max(50);

export const paginationInputSchema = z
  .object({
    offset: z.number().int().min(0).default(0),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();

export const dateRangeInputSchema = z
  .object({
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
  })
  .strict();

const queryBaseShape = {
  dateFrom: isoDateSchema
    .optional()
    .describe(
      'Inclusive start date YYYY-MM-DD. Omit both dates for the last 30 inclusive calendar days ending today. If set without dateTo, dateTo becomes today.',
    ),
  dateTo: isoDateSchema
    .optional()
    .describe(
      'Inclusive end date YYYY-MM-DD. If set without dateFrom, the range is all matching history until this day. Do not confuse with limit, which is page size.',
    ),
  offset: z
    .number()
    .int()
    .min(0)
    .default(0)
    .describe('Records to skip. Use nextOffset from the previous page to continue.'),
  limit: z.number().int().min(1).max(100).default(50).describe('Page size from 1 to 100. Defaults to 50.'),
};

const nativeCurrencyCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(16)
  .describe(
    'ISO-like currency code of the transaction amount (native currency, not necessarily the workspace default).',
  );

const includeCategoryIdsSchema = idListSchema.describe(
  'Keep records whose category is one of these ids or a child of one of them (OR). Use list_categories to resolve names.',
);
const excludeCategoryIdsSchema = idListSchema.describe(
  'Drop records whose category is one of these ids or a child of one of them.',
);

export const listToolArgumentsSchema = z.object({}).strict();

export const listAccountBalancesArgumentsSchema = z
  .object({
    accountId: positiveIdSchema.describe(
      'Account id from list_accounts. Required; balances are loaded for this account only.',
    ),
    currencyCode: z
      .string()
      .trim()
      .min(1)
      .max(16)
      .optional()
      .describe(
        'Restrict to this currency. Amounts stay in this code; they are not converted to the default currency.',
      ),
  })
  .strict();

export const queryExpensesArgumentsSchema = z
  .object({
    ...queryBaseShape,
    accountId: positiveIdSchema.optional().describe('Restrict to this account id from list_accounts.'),
    currencyCode: nativeCurrencyCodeSchema.optional(),
    includeCategoryIds: includeCategoryIdsSchema.optional(),
    excludeCategoryIds: excludeCategoryIdsSchema.optional(),
    amountFrom: z
      .number()
      .finite()
      .min(0)
      .optional()
      .describe('Minimum native amount in currencyCode, not converted to the default currency.'),
    amountTo: z
      .number()
      .finite()
      .min(0)
      .optional()
      .describe('Maximum native amount in currencyCode, not converted to the default currency.'),
    noteContains: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .optional()
      .describe('Case-insensitive substring match on the note.'),
    includeTagIds: idListSchema
      .optional()
      .describe('AND filter: the expense must have every listed tag id from list_tags.'),
    excludeTagIds: idListSchema.optional().describe('Drop expenses that have any of these tag ids.'),
  })
  .strict();

export const queryIncomesArgumentsSchema = z
  .object({
    ...queryBaseShape,
    accountId: positiveIdSchema.optional().describe('Restrict to this account id from list_accounts.'),
    currencyCode: nativeCurrencyCodeSchema.optional(),
    includeCategoryIds: includeCategoryIdsSchema.optional(),
    excludeCategoryIds: excludeCategoryIdsSchema.optional(),
    noteContains: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .optional()
      .describe('Case-insensitive substring match on the note.'),
  })
  .strict();

export const queryAccountTransfersArgumentsSchema = z
  .object({
    ...queryBaseShape,
    sourceAccountId: positiveIdSchema.optional().describe('Restrict to transfers leaving this account id.'),
    destinationAccountId: positiveIdSchema.optional().describe('Restrict to transfers arriving at this account id.'),
    currencyCode: nativeCurrencyCodeSchema.optional(),
  })
  .strict();

export const queryCurrencyExchangesArgumentsSchema = z
  .object({
    ...queryBaseShape,
    accountId: positiveIdSchema.optional().describe('Restrict to this account id from list_accounts.'),
    fromCurrencyCode: z.string().trim().min(1).max(16).optional().describe('Sold/source currency of the exchange.'),
    toCurrencyCode: z
      .string()
      .trim()
      .min(1)
      .max(16)
      .optional()
      .describe('Bought/destination currency of the exchange.'),
  })
  .strict();

export const financeToolArgumentsSchemas = {
  list_accounts: listToolArgumentsSchema,
  list_balances: listAccountBalancesArgumentsSchema,
  list_categories: listToolArgumentsSchema,
  list_currencies: listToolArgumentsSchema,
  list_tags: listToolArgumentsSchema,
  query_expenses: queryExpensesArgumentsSchema,
  query_incomes: queryIncomesArgumentsSchema,
  query_account_transfers: queryAccountTransfersArgumentsSchema,
  query_currency_exchanges: queryCurrencyExchangesArgumentsSchema,
} as const satisfies Record<FinanceToolName, z.ZodType>;

const accountReferenceSchema = z
  .object({
    id: positiveIdSchema,
    name: z.string(),
  })
  .strict();

const categoryReferenceSchema = z
  .object({
    id: positiveIdSchema,
    name: z.string(),
    parentId: z.union([positiveIdSchema, z.null()]).describe('Parent category id, or null for a top-level category.'),
    parentName: z.string().optional().describe('Present when parentId is set.'),
    type: z.enum(['income', 'expense']).describe('Whether this category is used on incomes or expenses.'),
  })
  .strict();

const currencyReferenceSchema = z
  .object({
    code: z.string().describe('Currency code used in query filters and on transaction records.'),
    name: z.string(),
    isDefault: z
      .boolean()
      .describe(
        'True for the single workspace default currency. Expense and income rate converts amount into this currency.',
      ),
  })
  .strict();

const tagReferenceSchema = z
  .object({
    id: positiveIdSchema,
    name: z.string(),
  })
  .strict();

const defaultCurrencyCodeSchema = z
  .string()
  .min(1)
  .max(16)
  .describe(
    'Workspace default currency. Expense and income rate is units of this currency per 1 currencyCode; rate is 1 when they match.',
  );

const nativeAmountSchema = z.number().finite().describe('Amount in currencyCode, the record native currency.');

const rateToDefaultSchema = z
  .number()
  .finite()
  .describe(
    'Units of defaultCurrencyCode per 1 unit of currencyCode. Equals 1 when currencyCode is the workspace default. amountInDefaultCurrency = amount * rate.',
  );

const amountInDefaultCurrencySchema = z
  .number()
  .finite()
  .describe('amount * rate, expressed in defaultCurrencyCode. Use this to compare mixed-currency records.');

export const listAccountsOutputSchema = withSingleTypeJsonSchema(
  z.object({ accounts: z.array(accountReferenceSchema) }).strict(),
);
export const listAccountBalancesOutputSchema = withSingleTypeJsonSchema(
  z
    .object({
      defaultCurrencyCode: defaultCurrencyCodeSchema,
      balances: z.array(
        z
          .object({
            account: accountReferenceSchema,
            currencyCode: z.string().describe('Currency of amount, incomes, and expenses on this row.'),
            amount: z
              .number()
              .finite()
              .describe(
                'Current stock in currencyCode (opening balance plus all incomes, expenses, transfers, exchanges, and credit/debt movement). Not converted to defaultCurrencyCode.',
              ),
            incomes: z
              .number()
              .finite()
              .describe('Lifetime income total in currencyCode, not converted and not limited to a date range.'),
            expenses: z
              .number()
              .finite()
              .describe('Lifetime expense total in currencyCode, not converted and not limited to a date range.'),
          })
          .strict(),
      ),
    })
    .strict(),
);
export const listCategoriesOutputSchema = withSingleTypeJsonSchema(
  z.object({ categories: z.array(categoryReferenceSchema) }).strict(),
);
export const listCurrenciesOutputSchema = withSingleTypeJsonSchema(
  z
    .object({
      defaultCurrencyCode: defaultCurrencyCodeSchema,
      currencies: z.array(currencyReferenceSchema),
    })
    .strict(),
);
export const listTagsOutputSchema = withSingleTypeJsonSchema(z.object({ tags: z.array(tagReferenceSchema) }).strict());

const pageMetadataShape = {
  defaultCurrencyCode: defaultCurrencyCodeSchema,
  totalCount: z.number().int().min(0).describe('Total matching records, not only this page.'),
  hasMore: z.boolean().describe('True when more records exist after this page.'),
  nextOffset: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe('Pass as offset to fetch the next page, or null when hasMore is false.'),
};

export const queryExpensesOutputSchema = withSingleTypeJsonSchema(
  z
    .object({
      items: z.array(
        z
          .object({
            id: positiveIdSchema,
            date: isoDateSchema,
            account: accountReferenceSchema,
            category: categoryReferenceSchema,
            amount: nativeAmountSchema,
            currencyCode: z.string().describe('Native currency of amount.'),
            rate: rateToDefaultSchema,
            amountInDefaultCurrency: amountInDefaultCurrencySchema,
            note: z.string(),
            tags: z.array(tagReferenceSchema).describe('Empty when the expense has no tags.'),
            mcc: z.number().int().positive().optional().describe('Optional card-network merchant category code.'),
          })
          .strict(),
      ),
      ...pageMetadataShape,
    })
    .strict(),
);

export const queryIncomesOutputSchema = withSingleTypeJsonSchema(
  z
    .object({
      items: z.array(
        z
          .object({
            id: positiveIdSchema,
            date: isoDateSchema,
            account: accountReferenceSchema,
            category: categoryReferenceSchema,
            amount: nativeAmountSchema,
            currencyCode: z.string().describe('Native currency of amount.'),
            rate: rateToDefaultSchema,
            amountInDefaultCurrency: amountInDefaultCurrencySchema,
            note: z.string(),
          })
          .strict(),
      ),
      ...pageMetadataShape,
    })
    .strict(),
);

export const queryAccountTransfersOutputSchema = withSingleTypeJsonSchema(
  z
    .object({
      items: z.array(
        z
          .object({
            id: positiveIdSchema,
            date: isoDateSchema,
            sourceAccount: accountReferenceSchema,
            destinationAccount: accountReferenceSchema,
            amount: z
              .number()
              .finite()
              .describe(
                'Amount moved, in currencyCode, identical on both accounts. Not converted to the default currency.',
              ),
            currencyCode: z.string().describe('Single currency of this transfer. There is no FX rate on transfers.'),
            note: z.union([z.string(), z.null()]),
          })
          .strict(),
      ),
      ...pageMetadataShape,
    })
    .strict(),
);

export const queryCurrencyExchangesOutputSchema = withSingleTypeJsonSchema(
  z
    .object({
      items: z.array(
        z
          .object({
            id: positiveIdSchema,
            date: isoDateSchema,
            account: accountReferenceSchema,
            fromAmount: z.number().finite().describe('Amount sold, in fromCurrencyCode.'),
            fromCurrencyCode: z.string(),
            toAmount: z.number().finite().describe('Amount bought, in toCurrencyCode.'),
            toCurrencyCode: z.string(),
            note: z.string(),
          })
          .strict(),
      ),
      ...pageMetadataShape,
    })
    .strict(),
);

export const financeToolOutputSchemas = {
  list_accounts: listAccountsOutputSchema,
  list_balances: listAccountBalancesOutputSchema,
  list_categories: listCategoriesOutputSchema,
  list_currencies: listCurrenciesOutputSchema,
  list_tags: listTagsOutputSchema,
  query_expenses: queryExpensesOutputSchema,
  query_incomes: queryIncomesOutputSchema,
  query_account_transfers: queryAccountTransfersOutputSchema,
  query_currency_exchanges: queryCurrencyExchangesOutputSchema,
} as const satisfies Record<FinanceToolName, z.ZodType>;

export const financeCommandSchema = z
  .object({
    requestId: requestIdSchema,
    name: financeToolNameSchema,
    arguments: jsonObjectSchema,
    expiresAt: isoDateTimeSchema,
  })
  .strict();
export type FinanceCommand = z.infer<typeof financeCommandSchema>;

export const pollCommandsResponseSchema = z
  .object({
    commands: z.array(financeCommandSchema).max(10),
    paired: z.boolean(),
  })
  .strict();
export type PollCommandsResponse = z.infer<typeof pollCommandsResponseSchema>;

const toolSuccessSchema = z
  .object({
    requestId: requestIdSchema,
    ok: z.literal(true),
    result: jsonValueSchema,
  })
  .strict();

const toolFailureSchema = z
  .object({
    requestId: requestIdSchema,
    ok: z.literal(false),
    error: z
      .object({
        code: z.string().trim().min(1).max(64),
        message: z.string().trim().min(1).max(500),
      })
      .strict(),
  })
  .strict();

export const submitToolResultSchema = z.discriminatedUnion('ok', [toolSuccessSchema, toolFailureSchema]);
export type SubmitToolResult = z.infer<typeof submitToolResultSchema>;

export const submitToolResultWithSessionKeySchema = z.discriminatedUnion('ok', [
  toolSuccessSchema.extend({ sessionKey: sessionKeySchema }),
  toolFailureSchema.extend({ sessionKey: sessionKeySchema }),
]);
export type SubmitToolResultWithSessionKey = z.infer<typeof submitToolResultWithSessionKeySchema>;

export const healthResponseSchema = z
  .object({
    status: z.literal('ok'),
    service: z.literal('personal-finance-mcp-relay'),
    version: z.string().trim().min(1),
  })
  .strict();
